import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface TierConfig {
  label: string;
  price: number;
  angbao_reward: number;
  withdraw_limit: number;
  date: string;
}

interface TierPurchaseStatus {
  approved: number;
  pending: number;
}

interface HistoryItem {
  id: number;
  tier: string;
  tier_label: string;
  amount: string;
  status: string;
  created_at: string;
}

const tierKeys = ['oct1', 'oct2', 'oct3', 'oct4'] as const;
type TierKey = typeof tierKeys[number];

const tierImages: Record<TierKey, string> = {
  oct1: '/images/guoqing_yi.png',
  oct2: '/images/guoqing_er.png',
  oct3: '/images/guoqing_san.png',
  oct4: '/images/guoqing_si.png',
};

const statusLabels: Record<string, string> = {
  pending: '待支付',
  approved: '已支付',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

const statusColors: Record<string, string> = {
  pending: 'text-yellow-600',
  approved: 'text-green-600',
  failed: 'text-red-500',
  expired: 'text-gray-400',
  cancelled: 'text-gray-400',
};

export default function Guoqing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<Record<TierKey, TierConfig> | null>(null);
  const [purchases, setPurchases] = useState<Record<string, TierPurchaseStatus>>({});
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<TierKey | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    api.get('/guoqing/status').then(res => {
      setTiers(res.data.tiers);
      setPurchases(res.data.purchased || {});
    }).catch(() => {
      toast.error('加载失败');
    }).finally(() => setLoading(false));
  }, []);

  function handleOpenTier(tier: TierKey) {
    const tierConfig = tiers?.[tier];
    if (tierConfig?.date) {
      const today = new Date().toISOString().slice(0, 10);
      if (today !== tierConfig.date) {
        toast.error(`${tierConfig.label}仅限当日办理`);
        return;
      }
    }
    const status = purchases[tier];
    if (status?.pending) {
      toast('您有待支付的订单，请稍候再试');
      return;
    }
    setSelectedTier(tier);
    setShowPayModal(true);
  }

  async function handlePurchase(product: 'WX' | 'ALI' | 'ALI2') {
    if (!selectedTier || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/guoqing/purchase', { tier: selectedTier, product });
      const payUrl = res.data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.error('支付链接获取失败');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '购买失败');
    } finally {
      setSubmitting(false);
      setShowPayModal(false);
    }
  }

  async function loadHistory() {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await api.get('/guoqing/history');
      setHistory(res.data || []);
    } catch {
      toast.error('加载记录失败');
    } finally {
      setHistoryLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-md mx-auto min-h-screen flex items-center justify-center" style={{ backgroundColor: '#8b0000' }}>
        <AuthGuard />
        <div className="text-white/60 text-sm">加载中...</div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen relative" style={{ backgroundColor: '#8b0000' }}>
      <AuthGuard />

      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
          className="absolute top-4 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-black/80"
      >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>

      <div className="relative">
        <img src="/images/guoqing_beijing.png" alt="" className="w-full" />
        <div className="absolute top-0 left-0 right-0 w-full" style={{ paddingTop: '400px' }}>
          <div className="flex justify-center gap-2 px-3 pb-2">
            {tierKeys.map(key => {
              const tier = tiers?.[key];
              if (!tier) return null;
              const today = new Date().toISOString().slice(0, 10);
              const isAvailable = !tier.date || today === tier.date;

              return (
                <div key={key} className="relative flex-1" style={{ opacity: isAvailable ? 1 : 0.45 }}>
                  <img
                    src={tierImages[key]}
                    alt={tier.label}
                    className="w-full rounded-lg"
                  />
                  <button
                    onClick={() => handleOpenTier(key)}
                    className="absolute"
                    style={{
                      bottom: '2%',
                      left: '10%',
                      width: '80%',
                      height: '10%',
                      background: 'transparent',
                      border: 'none',
                      cursor: isAvailable ? 'pointer' : 'not-allowed',
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Shuoming section */}
          <div className="px-3 mt-1">
            <img src="/images/guoqing_shuoming.png" alt="" className="w-full rounded-lg" />
          </div>

          {/* Purchase History Button */}
          <div className="px-6 py-4">
            <button
              onClick={loadHistory}
              className="w-full py-3 rounded-xl text-base font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #ec221e, #de1a17)' }}
            >
              办理记录
            </button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPayModal && selectedTier && tiers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !submitting && setShowPayModal(false)}>
          <div className="bg-white rounded-2xl w-[85%] max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-center text-lg font-bold text-[#333] mb-1">
              {tiers[selectedTier].label}
            </h3>
            <p className="text-center text-2xl font-bold text-[#c41a1a] mb-4">
              ¥{tiers[selectedTier].price}
            </p>
            <div className="space-y-2.5">
              <button
                onClick={() => handlePurchase('WX')}
                disabled={submitting}
                className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #07c160, #06ad56)' }}
              >
                {submitting ? '处理中...' : '微信支付'}
              </button>
              <button
                onClick={() => handlePurchase('ALI')}
                disabled={submitting}
                className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1677ff, #0958d9)' }}
              >
                {submitting ? '处理中...' : '支付宝支付'}
              </button>
              <button
                onClick={() => handlePurchase('ALI2')}
                disabled={submitting}
                className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1677ff, #0958d9)' }}
              >
                {submitting ? '处理中...' : '支付宝支付2'}
              </button>
            </div>
            <button
              onClick={() => setShowPayModal(false)}
              disabled={submitting}
              className="w-full mt-3 py-2 text-sm text-gray-400"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-2xl w-[90%] max-w-sm max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-center text-lg font-bold text-[#333]">办理记录</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {historyLoading ? (
                <div className="text-center text-gray-400 text-sm py-8">加载中...</div>
              ) : history.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-8">暂无记录</div>
              ) : (
                <div className="space-y-3">
                  {history.map(item => (
                    <div key={item.id} className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-[#333]">{item.tier_label}</span>
                        <span className={`text-xs font-bold ${statusColors[item.status] || 'text-gray-400'}`}>
                          {statusLabels[item.status] || item.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{item.created_at}</span>
                        <span className="text-sm font-bold text-[#c41a1a]">¥{item.amount}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setShowHistory(false)}
                className="w-full py-2.5 rounded-xl text-sm text-gray-500 bg-gray-100"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
